import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronDown, Wand2 } from 'lucide-react';
import ImageUpload from './ImageUpload';
import TranslatableItemFields from './TranslatableItemFields';
import AvailabilitySchedule from './AvailabilitySchedule';
import { storage } from '../utils/storage';
import { normalizeUnit, familyUnits } from '../utils/units';
import { recipeTotalCost, recipeMargin } from '../utils/recipeCost';
import { sameRecipeCost } from '../utils/itemForm';
import { formatCurrency } from '../utils/formatCurrency';
import { createInventoryItem } from '../services/api';
import HelpIcon from './help/HelpIcon';
import OrgRecipeLinesEditor from './OrgRecipeLinesEditor';
import { findOrgRecipeForItem, withIngredientNames } from '../utils/orgRecipes';
import './ItemForm.css';

const DRAFT_DEBOUNCE_MS = 1000;
const MODIFIER_BATCH_SIZE = 6;

const getDraftKey = (draftKey) => {
  const restaurantId = storage.get('selectedRestaurantId') || 'default';
  return `itemFormDraft_${restaurantId}_${draftKey}`;
};

const ItemForm = ({
  initialData,
  categories,
  modifierGroups,
  inventoryItems = [],
  onSubmit,
  onCancel,
  onDirtyChange,
  submitLabel,
  title,
  draftKey,
  focusSection,
  recipeAccess = null,
  orgRecipes = null,
  submitError = '',
}) => {
  const { t } = useTranslation();

  const defaultFormData = {
    categoryId: '',
    name: '',
    description: '',
    price: '',
    preparationTime: 15,
    tags: '',
    allergens: '',
    imageUrl: '',
    translations: {},
    availability: null,
    modifierGroups: [],
    recipe: [],
    // Vínculo de reventa (RES-29): `null` = el producto no se vende como un insumo tal cual
    inventoryLink: null,
    isCombo: false,
    comboItems: { fixed: [], groups: [] },
    isFeatured: false,
    isDailySpecial: false,
    isPromotion: false,
    originalPrice: '',
    promotionType: '',
    promotionBadge: '',
    highlightColor: '',
    promotionValidFrom: '',
    promotionValidTo: '',
    promotionSchedule: null,
    storyOrder: 0,
  };

  // Check for saved draft on mount
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const draftAppliedRef = useRef(false);

  const loadDraft = useCallback(() => {
    if (!draftKey) return null;
    try {
      const stored = storage.get(getDraftKey(draftKey));
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      if (parsed && parsed.formData && parsed.savedAt) {
        // Discard drafts older than 7 days
        const age = Date.now() - parsed.savedAt;
        if (age > 7 * 24 * 60 * 60 * 1000) {
          storage.remove(getDraftKey(draftKey));
          return null;
        }
        return parsed;
      }
    } catch {
      // Corrupted draft — remove it
      storage.remove(getDraftKey(draftKey));
    }
    return null;
  }, [draftKey]);

  const clearDraft = useCallback(() => {
    if (draftKey) {
      storage.remove(getDraftKey(draftKey));
    }
  }, [draftKey]);

  // Initialize form state, checking for draft
  const [formData, setFormData] = useState(() => {
    const base = { ...defaultFormData, ...initialData };
    if (!draftKey || draftAppliedRef.current) return base;
    const draft = loadDraft();
    if (draft) {
      draftAppliedRef.current = true;
      return { ...base, ...draft.formData };
    }
    return base;
  });

  // Show draft banner after mount if draft was restored
  useEffect(() => {
    if (draftAppliedRef.current) {
      setShowDraftBanner(true);
    }
  }, []);

  // Debounced autosave to localStorage
  const saveDraftTimeoutRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  useEffect(() => {
    if (!draftKey) return;

    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current);
    }

    saveDraftTimeoutRef.current = setTimeout(() => {
      try {
        const dataToSave = { ...formDataRef.current };
        // Don't save imageUrl in draft (can be large/stale)
        delete dataToSave.imageUrl;
        storage.set(
          getDraftKey(draftKey),
          JSON.stringify({ formData: dataToSave, savedAt: Date.now() })
        );
      } catch {
        // localStorage full or unavailable — silently skip
      }
    }, DRAFT_DEBOUNCE_MS);

    return () => {
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
      }
    };
  }, [formData, draftKey]);

  const handleDiscardDraft = useCallback(() => {
    clearDraft();
    setShowDraftBanner(false);
    setFormData({ ...defaultFormData, ...initialData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearDraft, initialData]);

  // Collapsible sections state — basicInfo always open by default. `focusSection` abre una
  // sección de entrada (p. ej. "Armar receta" desde la cobertura de inventario, RES-39).
  const [collapsedSections, setCollapsedSections] = useState({
    basicInfo: false,
    image: true,
    recipe: focusSection !== 'recipe',
    stock: true,
    combo: true,
    tagsAllergens: true,
    availability: true,
    modifiers: true,
    promotions: true,
  });

  const toggleSection = useCallback((section) => {
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const recipeSectionRef = useRef(null);
  useEffect(() => {
    if (focusSection === 'recipe' && recipeSectionRef.current?.scrollIntoView) {
      recipeSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [focusSection]);

  // Helper State for Combo Builder
  const [selectedFixedCategory, setSelectedFixedCategory] = useState('');
  const [selectedFixedItem, setSelectedFixedItem] = useState('');
  const [newGroup, setNewGroup] = useState({ name: '', min: 1, max: 1, items: [] });
  const [selectedGroupCategory, setSelectedGroupCategory] = useState('');
  const [selectedGroupItem, setSelectedGroupItem] = useState('');

  // Origen de la receta (RES-45): `null` = sin cambios; 'local' / 'org' = lo que eligió el usuario
  // con "Usar receta local" / "Volver a la de la organización". Solo entonces viaja `recipeSource`.
  const [recipeSourceChoice, setRecipeSourceChoice] = useState(null);
  const recipeBeforeOrgRef = useRef(null);

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        isCombo: initialData.isCombo || false,
        comboItems: initialData.comboItems || { fixed: [], groups: [] },
        recipe: initialData.recipe || [],
        inventoryLink: initialData.inventoryLink || null,
        isFeatured: initialData.isFeatured || false,
        isDailySpecial: initialData.isDailySpecial || false,
        isPromotion: initialData.isPromotion || false,
        originalPrice: initialData.originalPrice || '',
        promotionType: initialData.promotionType || '',
        promotionBadge: initialData.promotionBadge || '',
        highlightColor: initialData.highlightColor || '',
        promotionValidFrom: initialData.promotionValidFrom || '',
        promotionValidTo: initialData.promotionValidTo || '',
        promotionSchedule: initialData.promotionSchedule || null,
        storyOrder: initialData.storyOrder || 0,
      }));
    }
  }, [initialData]);

  // Track dirty state for unsaved changes warning
  const initialSnapshotRef = useRef(null);

  useEffect(() => {
    if (initialSnapshotRef.current === null) {
      initialSnapshotRef.current = JSON.stringify(formData);
    }
  }, [formData]);

  useEffect(() => {
    if (onDirtyChange && initialSnapshotRef.current !== null) {
      const isDirty = JSON.stringify(formData) !== initialSnapshotRef.current;
      onDirtyChange(isDirty);
    }
  }, [formData, onDirtyChange]);

  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.categoryId) {
      newErrors.categoryId = t('menu.validation.categoryRequired');
    }
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('menu.validation.nameRequired');
    }
    const parsedPrice = parseFloat(formData.price);
    if (
      formData.price === '' ||
      formData.price === null ||
      formData.price === undefined ||
      isNaN(parsedPrice)
    ) {
      newErrors.price = t('menu.validation.priceRequired');
    } else if (parsedPrice < 0) {
      newErrors.price = t('menu.validation.priceNegative');
    }
    if (!formData.isCombo && parseInt(formData.preparationTime, 10) < 0) {
      newErrors.preparationTime = t('menu.validation.prepTimeNegative');
    }
    // Vínculo de reventa (RES-29): con el switch en ON hay que elegir el insumo y una cantidad > 0
    if (!formData.isCombo && formData.inventoryLink) {
      if (!formData.inventoryLink.inventoryItemId) {
        newErrors.inventoryLink = t('menu.validation.resaleItemRequired');
      } else if (!(parseFloat(formData.inventoryLink.quantity) > 0)) {
        newErrors.inventoryLink = t('menu.validation.resaleQuantityInvalid');
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Auto-expand collapsed sections that contain error fields
      const sectionMap = {
        categoryId: 'basicInfo',
        name: 'basicInfo',
        price: 'basicInfo',
        preparationTime: 'basicInfo',
        inventoryLink: 'stock',
      };
      const sectionsToOpen = new Set();
      Object.keys(newErrors).forEach((field) => {
        const section = sectionMap[field];
        if (section && collapsedSections[section]) {
          sectionsToOpen.add(section);
        }
      });
      if (sectionsToOpen.size > 0) {
        setCollapsedSections((prev) => {
          const updated = { ...prev };
          sectionsToOpen.forEach((s) => {
            updated[s] = false;
          });
          return updated;
        });
      }
      return;
    }
    setErrors({});
    clearDraft();
    setShowDraftBanner(false);
    // Normalize tags/allergens to strings before submit
    const normalizedData = {
      ...formData,
      tags: Array.isArray(formData.tags) ? formData.tags.join(', ') : formData.tags || '',
      allergens: Array.isArray(formData.allergens)
        ? formData.allergens.join(', ')
        : formData.allergens || '',
      // Un combo consume por sus componentes (RES-32), nunca por vínculo de reventa: si el
      // producto pasó a combo, el vínculo se limpia en vez de quedar descontando a escondidas.
      inventoryLink:
        formData.isCombo || !formData.inventoryLink
          ? null
          : {
              inventoryItemId: formData.inventoryLink.inventoryItemId,
              quantity: parseFloat(formData.inventoryLink.quantity),
              unit: formData.inventoryLink.unit || undefined,
            },
    };
    // RES-45: la versión y la clave de la receta estándar las escribe el backend; el origen solo
    // viaja cuando el usuario eligió "Usar receta local" o "Volver a la de la organización".
    delete normalizedData.recipeSource;
    delete normalizedData.recipeVersion;
    delete normalizedData.recipeOrgKey;
    delete normalizedData.recipeSyncedAt;
    if (recipeSourceChoice) normalizedData.recipeSource = recipeSourceChoice;
    onSubmit(normalizedData);
  };

  // Lazy loading for modifier groups via IntersectionObserver
  const [visibleModifierCount, setVisibleModifierCount] = useState(MODIFIER_BATCH_SIZE);
  const modifierSentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = modifierSentinelRef.current;
    if (!sentinel || collapsedSections.modifiers) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleModifierCount((prev) => prev + MODIFIER_BATCH_SIZE);
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [collapsedSections.modifiers, visibleModifierCount]);

  const toggleModifierGroup = (groupId) => {
    const currentGroups = formData.modifierGroups || [];
    const newGroups = currentGroups.includes(groupId)
      ? currentGroups.filter((id) => id !== groupId)
      : [...currentGroups, groupId];
    setFormData({ ...formData, modifierGroups: newGroups });
  };

  // --- COMBO FUNCTIONS ---
  const addFixedItem = () => {
    if (!selectedFixedItem) return;
    const cat = categories.find((c) => c.categoryId === selectedFixedCategory);
    const item = cat?.items.find((i) => i.itemId === selectedFixedItem);
    if (!item) return;

    const newFixed = [
      ...(formData.comboItems.fixed || []),
      { itemId: item.itemId, name: item.name, quantity: 1 },
    ];
    setFormData({
      ...formData,
      comboItems: { ...formData.comboItems, fixed: newFixed },
    });
    setSelectedFixedItem('');
  };

  const removeFixedItem = (index) => {
    const newFixed = [...(formData.comboItems.fixed || [])];
    newFixed.splice(index, 1);
    setFormData({
      ...formData,
      comboItems: { ...formData.comboItems, fixed: newFixed },
    });
  };

  const addGroupItem = () => {
    if (!selectedGroupItem) return;
    const cat = categories.find((c) => c.categoryId === selectedGroupCategory);
    const item = cat?.items.find((i) => i.itemId === selectedGroupItem);
    if (!item) return;

    if (newGroup.items.some((i) => i.itemId === item.itemId)) return;

    setNewGroup({
      ...newGroup,
      items: [...newGroup.items, { itemId: item.itemId, name: item.name, extraPrice: 0 }],
    });
    setSelectedGroupItem('');
  };

  const addGroup = () => {
    if (!newGroup.name || newGroup.items.length === 0) return;
    const newGroups = [...(formData.comboItems.groups || []), { ...newGroup }];
    setFormData({
      ...formData,
      comboItems: { ...formData.comboItems, groups: newGroups },
    });
    setNewGroup({ name: '', min: 1, max: 1, items: [] });
  };

  const removeGroup = (index) => {
    const newGroups = [...(formData.comboItems.groups || [])];
    newGroups.splice(index, 1);
    setFormData({
      ...formData,
      comboItems: { ...formData.comboItems, groups: newGroups },
    });
  };

  // --- RECIPE FUNCTIONS ---
  // El editor de líneas vive en OrgRecipeLinesEditor (compartido con las recetas estándar, RES-45)
  const setRecipeLines = (lines) => {
    setFormData((prev) => ({ ...prev, recipe: lines }));
  };

  // El backend calcula el costo con el avgCost del local (RES-30). Mientras la receta no se
  // toque se muestra ese número; al editarla se recalcula en vivo con la misma fórmula.
  const savedRecipeCost =
    initialData && typeof initialData.recipeCost === 'number' ? initialData.recipeCost : null;
  // RES-65: acá se compara solo lo que determina el costo. Tildar Crítico cambia la receta
  // (`sameRecipe`, que decide si hay que reenviarla) pero no su costo, así que no tiene que
  // hacer saltar el número que calculó el backend con el avgCost del local.
  const recipeCost =
    savedRecipeCost !== null && sameRecipeCost(initialData.recipe, formData.recipe)
      ? savedRecipeCost
      : recipeTotalCost(formData.recipe, inventoryItems);
  const recipeMarginInfo =
    formData.recipe && formData.recipe.length > 0 ? recipeMargin(formData.price, recipeCost) : null;

  // --- ORIGEN DE LA RECETA (RES-45) ---
  const savedRecipeSource = initialData?.recipeSource;
  const isExistingItem = Boolean(initialData?.itemId);
  // Con `recipesLocked` la sucursal no edita recetas (RES-28): solo quien administra toda la
  // organización (`orgScoped`). Sin datos de acceso se deja editar y el backend decide.
  const recipesLocked = recipeAccess?.recipesLocked === true && recipeAccess?.orgScoped === false;
  // RES-103: con el candado, un producto que sigue la estándar tampoco lo edita quien administra
  // toda la organización (lo desengancharía del estándar); se cambia desde "Recetas estándar".
  const orgRecipeLocked =
    recipeAccess?.recipesLocked === true && !recipesLocked && savedRecipeSource === 'org';
  const matchingOrgRecipe = isExistingItem
    ? findOrgRecipeForItem(orgRecipes, {
        name: formData.name,
        recipeOrgKey: initialData?.recipeOrgKey,
      })
    : null;
  const recipeReadOnly = recipesLocked || orgRecipeLocked || recipeSourceChoice === 'org';

  const chooseLocalRecipe = () => setRecipeSourceChoice('local');

  const chooseOrgRecipe = () => {
    if (!matchingOrgRecipe) return;
    recipeBeforeOrgRef.current = formData.recipe || [];
    setRecipeSourceChoice('org');
    setRecipeLines(withIngredientNames(matchingOrgRecipe.recipe, inventoryItems));
  };

  const undoRecipeSourceChoice = () => {
    if (recipeSourceChoice === 'org' && recipeBeforeOrgRef.current) {
      setRecipeLines(recipeBeforeOrgRef.current);
    }
    recipeBeforeOrgRef.current = null;
    setRecipeSourceChoice(null);
  };

  const renderRecipeSource = () => {
    if (recipeSourceChoice) {
      return (
        <div className="recipe-source-bar" role="status">
          <span className="recipe-source-note">
            {recipeSourceChoice === 'local'
              ? t('menu.recipe.orgSource.localPending')
              : t('menu.recipe.orgSource.orgPending', { version: matchingOrgRecipe?.version })}
          </span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={undoRecipeSourceChoice}>
            {t('common.undo')}
          </button>
        </div>
      );
    }
    if (savedRecipeSource === 'org') {
      return (
        <div className="recipe-source-bar">
          <span className="recipe-source-badge recipe-source-badge--org">
            {t('menu.recipe.orgSource.orgBadge', { version: initialData?.recipeVersion ?? '—' })}
          </span>
          <HelpIcon topicKey="inventory.recipeSource" />
          {recipesLocked ? (
            <span className="recipe-source-note">{t('menu.recipe.orgSource.lockedOrgNote')}</span>
          ) : orgRecipeLocked ? (
            <span className="recipe-source-note">
              {t('menu.recipe.orgSource.lockedOrgAdminNote')}
            </span>
          ) : (
            <button type="button" className="btn btn-sm btn-secondary" onClick={chooseLocalRecipe}>
              {t('menu.recipe.orgSource.useLocal')}
            </button>
          )}
        </div>
      );
    }
    const canAdoptOrg = Boolean(matchingOrgRecipe) && !recipesLocked;
    if (savedRecipeSource !== 'local' && !canAdoptOrg && !recipesLocked) return null;
    return (
      <div className="recipe-source-bar">
        {savedRecipeSource === 'local' && (
          <span className="recipe-source-badge">{t('menu.recipe.orgSource.localBadge')}</span>
        )}
        {(savedRecipeSource === 'local' || canAdoptOrg) && (
          <HelpIcon topicKey="inventory.recipeSource" />
        )}
        {recipesLocked && (
          <span className="recipe-source-note">{t('menu.recipe.orgSource.lockedNote')}</span>
        )}
        {canAdoptOrg && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={chooseOrgRecipe}>
            {savedRecipeSource === 'local'
              ? t('menu.recipe.orgSource.backToOrg')
              : t('menu.recipe.orgSource.adoptOrg')}
          </button>
        )}
      </div>
    );
  };

  // --- VÍNCULO DE REVENTA (RES-29) ---
  // Insumos creados desde acá ("insumo espejo"): se suman a los que llegan por prop para que el
  // select los muestre sin recargar la página.
  const [createdInventoryItems, setCreatedInventoryItems] = useState([]);
  const [creatingMirror, setCreatingMirror] = useState(false);
  const [mirrorError, setMirrorError] = useState('');

  const linkableInventoryItems = [...inventoryItems, ...createdInventoryItems];
  const linkedInventoryItem = formData.inventoryLink
    ? linkableInventoryItems.find((i) => i.itemId === formData.inventoryLink.inventoryItemId)
    : null;

  const setInventoryLink = (patch) => {
    setFormData((prev) => ({ ...prev, inventoryLink: { ...prev.inventoryLink, ...patch } }));
  };

  const toggleInventoryLink = () => {
    setMirrorError('');
    setFormData((prev) => ({
      ...prev,
      inventoryLink: prev.inventoryLink ? null : { inventoryItemId: '', quantity: 1, unit: '' },
    }));
  };

  const handleSelectLinkedItem = (e) => {
    const id = e.target.value;
    const item = linkableInventoryItems.find((i) => i.itemId === id);
    setInventoryLink({
      inventoryItemId: id,
      // Propone la unidad del insumo; el selector deja elegir otras de la misma familia (RES-17)
      unit: item ? normalizeUnit(item.unit) || 'un' : '',
    });
  };

  // Crea un insumo de tipo RESALE con el nombre del producto y lo deja vinculado, para el caso
  // típico: una bebida que todavía no existe en el inventario.
  const createMirrorInventoryItem = async () => {
    if (!formData.name || !formData.name.trim()) {
      setMirrorError(t('menu.resaleLink.mirrorNeedsName'));
      return;
    }
    setCreatingMirror(true);
    setMirrorError('');
    try {
      const created = await createInventoryItem({
        name: formData.name.trim(),
        kind: 'RESALE',
        unit: 'un',
        trackStock: true,
        stock: 0,
        refCost: 0,
        defaultMinStock: 0,
        category: null,
        supplierId: null,
        sku: null,
      });
      if (!created?.itemId) throw new Error('missing itemId');
      setCreatedInventoryItems((prev) => [...prev, created]);
      setFormData((prev) => ({
        ...prev,
        inventoryLink: {
          inventoryItemId: created.itemId,
          quantity: Number(prev.inventoryLink?.quantity) > 0 ? prev.inventoryLink.quantity : 1,
          unit: normalizeUnit(created.unit) || 'un',
        },
      }));
    } catch {
      setMirrorError(t('menu.resaleLink.mirrorError'));
    } finally {
      setCreatingMirror(false);
    }
  };

  // Vista previa de consumo (RES-30): qué descuenta una unidad vendida de este producto.
  const consumptionPreview = [
    ...(formData.inventoryLink?.inventoryItemId
      ? [
          {
            key: 'link',
            quantity: formData.inventoryLink.quantity,
            unit: formData.inventoryLink.unit,
            name:
              linkableInventoryItems.find(
                (i) => i.itemId === formData.inventoryLink.inventoryItemId
              )?.name || formData.inventoryLink.inventoryItemId,
            isCritical: false,
          },
        ]
      : []),
    ...(formData.recipe || []).map((line, idx) => ({
      key: 'recipe-' + idx + '-' + line.inventoryItemId,
      quantity: line.quantity,
      unit: line.unit,
      name:
        line.name ||
        inventoryItems.find((i) => i.itemId === line.inventoryItemId)?.name ||
        line.inventoryItemId,
      isCritical: line.isCritical === true,
    })),
  ];

  return (
    <div className="card">
      {title && <h3>{title}</h3>}
      <form onSubmit={handleSubmit} noValidate>
        {showDraftBanner && (
          <div className="draft-banner" role="status">
            <span>{t('menu.draftRestored')}</span>
            <button type="button" className="btn btn-sm btn-ghost" onClick={handleDiscardDraft}>
              {t('menu.draftDiscard')}
            </button>
          </div>
        )}
        <p className="required-legend">
          <span className="required">*</span> {t('common.requiredFieldsLegend')}
        </p>

        {/* ── Tipo de producto (pill toggle) ── */}
        <div className="form-section">
          <div className="item-type-toggle" role="group" aria-label={t('menu.productType')}>
            <button
              type="button"
              className={!formData.isCombo ? 'active' : ''}
              onClick={() => setFormData({ ...formData, isCombo: false })}
              aria-pressed={!formData.isCombo}
            >
              {t('menu.itemForm.standardProduct')}
            </button>
            <button
              type="button"
              className={formData.isCombo ? 'active' : ''}
              onClick={() => setFormData({ ...formData, isCombo: true })}
              aria-pressed={formData.isCombo}
            >
              {t('menu.itemForm.combo')}
            </button>
          </div>
          {formData.isCombo && !initialData?.itemId && (
            <div className="combo-template-banner" role="note">
              <Wand2 size={18} aria-hidden="true" />
              <div>
                <strong>{t('menu.comboBannerTitle')}</strong>
                <span>{t('menu.comboBannerText')}</span>
              </div>
              <Link to="/menu/combos/new" className="btn btn-primary btn-sm">
                {t('menu.comboBannerCta')}
              </Link>
            </div>
          )}
        </div>

        {/* ── Información Básica ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.basicInfo ? 'collapsed' : ''}`}
            onClick={() => toggleSection('basicInfo')}
            aria-expanded={!collapsedSections.basicInfo}
          >
            <h2>{t('menu.itemForm.basicInfo')}</h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.basicInfo ? 'section-body--collapsed' : ''}`}
          >
            <div className="form-group">
              <label htmlFor="item-category">
                {t('menu.category')} <span className="required">*</span>
              </label>
              <select
                id="item-category"
                value={formData.categoryId}
                onChange={(e) => {
                  setFormData({ ...formData, categoryId: e.target.value });
                  if (errors.categoryId) setErrors((prev) => ({ ...prev, categoryId: undefined }));
                }}
              >
                <option value="">{t('menu.selectCategory')}</option>
                {categories.map((cat) => (
                  <option key={cat.categoryId} value={cat.categoryId}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && <span className="field-error">{errors.categoryId}</span>}
            </div>

            <TranslatableItemFields
              item={formData}
              onChange={(updatedItem) => {
                setFormData(updatedItem);
                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              nameRequired={true}
              nameError={errors.name}
            />

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="item-price">
                  {t('menu.price')} <span className="required">*</span>
                </label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    id="item-price"
                    type="number"
                    step="1"
                    min="0"
                    value={formData.price}
                    onChange={(e) => {
                      setFormData({ ...formData, price: e.target.value });
                      if (errors.price) setErrors((prev) => ({ ...prev, price: undefined }));
                    }}
                    placeholder="0"
                  />
                </div>
                {errors.price && <span className="field-error">{errors.price}</span>}
              </div>

              {!formData.isCombo && (
                <div className="form-group">
                  <label htmlFor="item-prep-time">
                    {t('menu.preparationTime')} (min) <span className="required">*</span>
                  </label>
                  <input
                    id="item-prep-time"
                    type="number"
                    min="0"
                    value={formData.preparationTime}
                    onChange={(e) => {
                      setFormData({ ...formData, preparationTime: e.target.value });
                      if (errors.preparationTime)
                        setErrors((prev) => ({ ...prev, preparationTime: undefined }));
                    }}
                  />
                  {errors.preparationTime && (
                    <span className="field-error">{errors.preparationTime}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Imagen ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.image ? 'collapsed' : ''}`}
            onClick={() => toggleSection('image')}
            aria-expanded={!collapsedSections.image}
          >
            <h2>
              {t('menu.coverImage')} <span className="optional-hint">({t('common.optional')})</span>
            </h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.image ? 'section-body--collapsed' : ''}`}
          >
            <ImageUpload
              resourceType="items"
              resourceId={formData.itemId || `temp-${Date.now()}`}
              currentImageUrl={formData.imageUrl}
              onImageUploaded={(url) => setFormData({ ...formData, imageUrl: url })}
            />
          </div>
        </div>

        {/* ── Receta e Ingredientes (solo estándar) ── */}
        {!formData.isCombo && (
          <div className="form-section" ref={recipeSectionRef}>
            <button
              type="button"
              className={`section-toggle ${collapsedSections.recipe ? 'collapsed' : ''}`}
              onClick={() => toggleSection('recipe')}
              aria-expanded={!collapsedSections.recipe}
            >
              <h2>{t('menu.recipe.sectionTitle')}</h2>
              {savedRecipeSource === 'org' && !recipeSourceChoice && (
                <span className="recipe-source-badge recipe-source-badge--org recipe-source-badge--header">
                  {t('menu.recipe.orgSource.orgBadge', {
                    version: initialData?.recipeVersion ?? '—',
                  })}
                </span>
              )}
              <ChevronDown className="section-toggle-icon" size={20} />
            </button>
            <div
              className={`section-body ${collapsedSections.recipe ? 'section-body--collapsed' : ''}`}
            >
              <div className="recipe-builder">
                <h4>
                  <span className="recipe-title">
                    {t('menu.recipe.ingredients')}
                    <HelpIcon topicKey="menu.recipeCost" />
                  </span>
                  <span className="cost-label">
                    {t('menu.recipe.estimatedCost')}: <strong>{formatCurrency(recipeCost)}</strong>
                    {recipeMarginInfo && (
                      <span
                        className={`recipe-margin${recipeMarginInfo.amount < 0 ? ' recipe-margin--negative' : ''}`}
                      >
                        {' · '}
                        {t('menu.recipe.margin')}: {formatCurrency(recipeMarginInfo.amount)} (
                        {recipeMarginInfo.percent}%)
                      </span>
                    )}
                  </span>
                </h4>

                {renderRecipeSource()}

                <OrgRecipeLinesEditor
                  lines={formData.recipe}
                  onChange={setRecipeLines}
                  inventoryItems={inventoryItems}
                  readOnly={recipeReadOnly}
                />

                {consumptionPreview.length > 0 && (
                  <div className="recipe-consumption-preview">
                    <h5>
                      {t('menu.recipe.consumptionTitle')}
                      <HelpIcon topicKey="menu.criticalIngredient" />
                    </h5>
                    <ul>
                      {consumptionPreview.map((line) => (
                        <li key={line.key}>
                          {line.quantity} {line.unit} {t('menu.recipe.consumptionOf')} {line.name}
                          {line.isCritical && (
                            <span className="recipe-critical-badge">
                              {t('menu.recipe.criticalBadge')}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Stock: vínculo de reventa (solo estándar, RES-29) ── */}
        {!formData.isCombo && (
          <div className="form-section">
            <button
              type="button"
              className={`section-toggle ${collapsedSections.stock ? 'collapsed' : ''}`}
              onClick={() => toggleSection('stock')}
              aria-expanded={!collapsedSections.stock}
            >
              <h2>{t('menu.resaleLink.sectionTitle')}</h2>
              <ChevronDown className="section-toggle-icon" size={20} />
            </button>
            <div
              className={`section-body ${collapsedSections.stock ? 'section-body--collapsed' : ''}`}
            >
              <div className="resale-link">
                <div className="resale-link-switch">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={!!formData.inventoryLink}
                      onChange={toggleInventoryLink}
                      aria-label={t('menu.resaleLink.toggleLabel')}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span className="resale-link-switch-label">
                    {t('menu.resaleLink.toggleLabel')}
                    <HelpIcon topicKey="menu.resaleLink" />
                  </span>
                </div>
                <p className="text-muted small">{t('menu.resaleLink.toggleHelp')}</p>

                {formData.inventoryLink && (
                  <>
                    <div className="recipe-form-row">
                      <div className="recipe-form-field flex-2">
                        <label htmlFor="resale-item">{t('menu.resaleLink.itemLabel')}</label>
                        <select
                          id="resale-item"
                          value={formData.inventoryLink.inventoryItemId || ''}
                          onChange={handleSelectLinkedItem}
                        >
                          <option value="">{t('menu.resaleLink.selectItem')}</option>
                          {linkableInventoryItems.map((item) => (
                            <option key={item.itemId} value={item.itemId}>
                              {item.name} ({normalizeUnit(item.unit) || item.unit})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="recipe-form-field flex-1">
                        <label htmlFor="resale-qty">{t('menu.resaleLink.quantity')}</label>
                        <input
                          id="resale-qty"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.inventoryLink.quantity ?? ''}
                          onChange={(e) => setInventoryLink({ quantity: e.target.value })}
                        />
                      </div>
                      <div className="recipe-form-field flex-1">
                        <label htmlFor="resale-unit">{t('menu.resaleLink.unit')}</label>
                        <select
                          id="resale-unit"
                          value={formData.inventoryLink.unit || ''}
                          onChange={(e) => setInventoryLink({ unit: e.target.value })}
                          disabled={!formData.inventoryLink.inventoryItemId}
                        >
                          {(formData.inventoryLink.unit
                            ? familyUnits(formData.inventoryLink.unit)
                            : ['']
                          ).map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {!formData.inventoryLink.inventoryItemId && (
                      <div className="resale-link-mirror">
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={createMirrorInventoryItem}
                          disabled={creatingMirror}
                        >
                          {creatingMirror
                            ? t('common.loading')
                            : t('menu.resaleLink.createMirrorItem')}
                        </button>
                        <span className="text-muted small">
                          {t('menu.resaleLink.createMirrorHelp')}
                        </span>
                      </div>
                    )}
                    {mirrorError && <span className="field-error">{mirrorError}</span>}
                    {errors.inventoryLink && (
                      <span className="field-error">{errors.inventoryLink}</span>
                    )}
                    {linkedInventoryItem && formData.recipe && formData.recipe.length > 0 && (
                      <p className="text-muted small">{t('menu.resaleLink.alsoHasRecipe')}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Configuración de Combo (solo combo) ── */}
        {formData.isCombo && (
          <div className="form-section">
            <button
              type="button"
              className={`section-toggle ${collapsedSections.combo ? 'collapsed' : ''}`}
              onClick={() => toggleSection('combo')}
              aria-expanded={!collapsedSections.combo}
            >
              <h2>{t('menu.itemForm.comboConfig.title')}</h2>
              <ChevronDown className="section-toggle-icon" size={20} />
            </button>
            <div
              className={`section-body ${collapsedSections.combo ? 'section-body--collapsed' : ''}`}
            >
              <div className="combo-builder">
                {/* Fixed Items */}
                <div className="combo-fixed-items">
                  <h5>{t('menu.itemForm.comboConfig.fixedItems')}</h5>
                  <div className="recipe-form-row">
                    <select
                      value={selectedFixedCategory}
                      onChange={(e) => setSelectedFixedCategory(e.target.value)}
                      className="flex-1"
                      aria-label={t('menu.comboFixedCategory')}
                    >
                      <option value="">{t('menu.selectCategory')}</option>
                      {categories
                        .filter((c) => c.items && c.items.length > 0)
                        .map((c) => (
                          <option key={c.categoryId} value={c.categoryId}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    <select
                      value={selectedFixedItem}
                      onChange={(e) => setSelectedFixedItem(e.target.value)}
                      disabled={!selectedFixedCategory}
                      className="flex-1"
                      aria-label={t('menu.comboFixedItem')}
                    >
                      <option value="">{t('menu.selectItem')}</option>
                      {selectedFixedCategory &&
                        categories
                          .find((c) => c.categoryId === selectedFixedCategory)
                          ?.items.map((i) => (
                            <option key={i.itemId} value={i.itemId}>
                              {i.name}
                            </option>
                          ))}
                    </select>
                    <button
                      type="button"
                      onClick={addFixedItem}
                      className="btn btn-sm btn-primary"
                      disabled={!selectedFixedItem}
                    >
                      {t('menu.itemForm.comboConfig.add')}
                    </button>
                  </div>

                  {formData.comboItems?.fixed?.length > 0 ? (
                    <ul className="list-group">
                      {formData.comboItems.fixed.map((item, idx) => (
                        <li key={idx} className="list-group-item">
                          {item.name} (x{item.quantity})
                          <button
                            type="button"
                            onClick={() => removeFixedItem(idx)}
                            className="btn btn-sm btn-danger"
                            aria-label={t('menu.itemForm.comboConfig.removeFixedItem', {
                              name: item.name,
                            })}
                          >
                            X
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted small">
                      {t('menu.itemForm.comboConfig.noFixedItems')}
                    </p>
                  )}
                </div>

                {/* Choice Groups */}
                <h5>{t('menu.itemForm.comboConfig.choiceGroups')}</h5>
                <div className="combo-new-group">
                  <h6>{t('menu.itemForm.comboConfig.newGroup')}</h6>
                  <label htmlFor="combo-group-name" className="sr-only">
                    {t('menu.comboGroupName')}
                  </label>
                  <input
                    id="combo-group-name"
                    type="text"
                    placeholder={t('menu.itemForm.comboConfig.groupNamePlaceholder')}
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  />
                  <div className="combo-min-max-row">
                    <label>
                      {t('menu.itemForm.comboConfig.min')}:{' '}
                      <input
                        type="number"
                        min="0"
                        value={newGroup.min}
                        onChange={(e) =>
                          setNewGroup({ ...newGroup, min: parseInt(e.target.value) })
                        }
                      />
                    </label>
                    <label>
                      {t('menu.itemForm.comboConfig.max')}:{' '}
                      <input
                        type="number"
                        min="1"
                        value={newGroup.max}
                        onChange={(e) =>
                          setNewGroup({ ...newGroup, max: parseInt(e.target.value) })
                        }
                      />
                    </label>
                  </div>
                  <div className="combo-item-select-row">
                    <select
                      value={selectedGroupCategory}
                      onChange={(e) => setSelectedGroupCategory(e.target.value)}
                      aria-label={t('menu.comboGroupCategory')}
                    >
                      <option value="">{t('menu.selectCategory')}</option>
                      {categories
                        .filter((c) => c.items && c.items.length > 0)
                        .map((c) => (
                          <option key={c.categoryId} value={c.categoryId}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    <select
                      value={selectedGroupItem}
                      onChange={(e) => setSelectedGroupItem(e.target.value)}
                      disabled={!selectedGroupCategory}
                      aria-label={t('menu.comboGroupItem')}
                    >
                      <option value="">{t('menu.selectItem')}</option>
                      {selectedGroupCategory &&
                        categories
                          .find((c) => c.categoryId === selectedGroupCategory)
                          ?.items.map((i) => (
                            <option key={i.itemId} value={i.itemId}>
                              {i.name}
                            </option>
                          ))}
                    </select>
                    <button
                      type="button"
                      onClick={addGroupItem}
                      className="btn btn-sm btn-secondary"
                      disabled={!selectedGroupItem}
                      aria-label={t('menu.addGroupItem')}
                    >
                      +
                    </button>
                  </div>
                  {newGroup.items.length > 0 && (
                    <ul className="combo-group-items-preview">
                      {newGroup.items.map((i, idx) => (
                        <li key={idx}>{i.name}</li>
                      ))}
                    </ul>
                  )}
                  <button type="button" onClick={addGroup} className="btn btn-sm btn-success">
                    {t('menu.itemForm.comboConfig.addGroup')}
                  </button>
                </div>

                {formData.comboItems?.groups?.length > 0 &&
                  formData.comboItems.groups.map((g, idx) => (
                    <div key={idx} className="combo-group-card">
                      <div>
                        <strong>{g.name}</strong>{' '}
                        {t('menu.itemForm.comboConfig.groupLimits', { min: g.min, max: g.max })}
                        <ul>
                          {g.items.map((i, iIdx) => (
                            <li key={iIdx}>{i.name}</li>
                          ))}
                        </ul>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeGroup(idx)}
                        className="btn btn-sm btn-danger"
                      >
                        {t('menu.itemForm.comboConfig.removeGroup')}
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tags y Alérgenos ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.tagsAllergens ? 'collapsed' : ''}`}
            onClick={() => toggleSection('tagsAllergens')}
            aria-expanded={!collapsedSections.tagsAllergens}
          >
            <h2>{t('menu.itemForm.tagsAndAllergens')}</h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.tagsAllergens ? 'section-body--collapsed' : ''}`}
          >
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="item-tags">
                  {t('menu.itemForm.tagsLabel')}{' '}
                  <span className="optional-hint">({t('common.optional')})</span>
                </label>
                <input
                  id="item-tags"
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder={t('menu.itemForm.tagsPlaceholder')}
                />
                <span className="help-text">{t('menu.itemForm.commaSeparated')}</span>
              </div>
              <div className="form-group">
                <label htmlFor="item-allergens">
                  {t('menu.allergens')}{' '}
                  <span className="optional-hint">({t('common.optional')})</span>
                </label>
                <input
                  id="item-allergens"
                  type="text"
                  value={formData.allergens}
                  onChange={(e) => setFormData({ ...formData, allergens: e.target.value })}
                  placeholder={t('menu.itemForm.allergensPlaceholder')}
                />
                <span className="help-text">{t('menu.itemForm.commaSeparated')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Disponibilidad ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.availability ? 'collapsed' : ''}`}
            onClick={() => toggleSection('availability')}
            aria-expanded={!collapsedSections.availability}
          >
            <h2>{t('menu.availability')}</h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.availability ? 'section-body--collapsed' : ''}`}
          >
            <AvailabilitySchedule
              value={formData.availability}
              onChange={(availability) => setFormData({ ...formData, availability })}
              label=""
            />
          </div>
        </div>

        {/* ── Modificadores (solo estándar) ── */}
        {!formData.isCombo && (
          <div className="form-section">
            <button
              type="button"
              className={`section-toggle ${collapsedSections.modifiers ? 'collapsed' : ''}`}
              onClick={() => toggleSection('modifiers')}
              aria-expanded={!collapsedSections.modifiers}
            >
              <h2>{t('menu.modifierGroups', 'Grupos de Modificadores')}</h2>
              <ChevronDown className="section-toggle-icon" size={20} />
            </button>
            <div
              className={`section-body ${collapsedSections.modifiers ? 'section-body--collapsed' : ''}`}
            >
              <div className="modifier-groups-list">
                {modifierGroups.slice(0, visibleModifierCount).map((group) => (
                  <div key={group.groupId} className="form-check">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        checked={(formData.modifierGroups || []).includes(group.groupId)}
                        onChange={() => toggleModifierGroup(group.groupId)}
                      />
                      <span className="modifier-group-info">
                        <span className="modifier-group-name">{group.name}</span>
                        <span className="modifier-group-hint">
                          {group.required
                            ? t('common.required', 'Obligatorio')
                            : t('common.optional', 'Opcional')}
                          {' · '}
                          {t('modifiers.minSelect', 'Mín')}: {group.minSelect} /{' '}
                          {t('modifiers.maxSelect', 'Máx')}: {group.maxSelect}
                          {group.options?.length > 0 &&
                            ` · ${group.options.map((o) => o.name).join(', ')}`}
                        </span>
                      </span>
                    </label>
                  </div>
                ))}
                {visibleModifierCount < modifierGroups.length && (
                  <div ref={modifierSentinelRef} className="modifier-sentinel" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Promociones y Destacados ── */}
        <div className="form-section">
          <button
            type="button"
            className={`section-toggle ${collapsedSections.promotions ? 'collapsed' : ''}`}
            onClick={() => toggleSection('promotions')}
            aria-expanded={!collapsedSections.promotions}
          >
            <h2>{t('menu.itemForm.promotions.title')}</h2>
            <ChevronDown className="section-toggle-icon" size={20} />
          </button>
          <div
            className={`section-body ${collapsedSections.promotions ? 'section-body--collapsed' : ''}`}
          >
            <div className="promo-builder">
              <div className="promo-checkboxes-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                  />
                  <span>{t('menu.itemForm.promotions.featured')}</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isDailySpecial}
                    onChange={(e) => setFormData({ ...formData, isDailySpecial: e.target.checked })}
                  />
                  <span>{t('menu.itemForm.promotions.dailySpecial')}</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isPromotion}
                    onChange={(e) => setFormData({ ...formData, isPromotion: e.target.checked })}
                  />
                  <span>{t('menu.itemForm.promotions.isPromotion')}</span>
                </label>
              </div>

              {formData.isFeatured && (
                <div className="featured-order">
                  <div className="form-group">
                    <label htmlFor="item-story-order">
                      {t('menu.itemForm.promotions.storyOrder')}
                    </label>
                    <input
                      id="item-story-order"
                      type="number"
                      min="0"
                      value={formData.storyOrder}
                      onChange={(e) =>
                        setFormData({ ...formData, storyOrder: parseInt(e.target.value) || 0 })
                      }
                      placeholder="0"
                    />
                  </div>
                </div>
              )}

              {formData.isPromotion && (
                <div className="promo-details">
                  <div className="form-group">
                    <label htmlFor="item-original-price">
                      {t('menu.itemForm.promotions.originalPrice')}
                    </label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        id="item-original-price"
                        type="number"
                        step="1"
                        value={formData.originalPrice}
                        onChange={(e) =>
                          setFormData({ ...formData, originalPrice: e.target.value })
                        }
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="item-promo-type">{t('menu.itemForm.promotions.type')}</label>
                    <select
                      id="item-promo-type"
                      value={formData.promotionType}
                      onChange={(e) => setFormData({ ...formData, promotionType: e.target.value })}
                    >
                      <option value="">{t('menu.itemForm.promotions.selectType')}</option>
                      <option value="descuento">
                        {t('menu.itemForm.promotions.types.descuento')}
                      </option>
                      <option value="happy_hour">
                        {t('menu.itemForm.promotions.types.happy_hour')}
                      </option>
                      <option value="2x1">{t('menu.itemForm.promotions.types.2x1')}</option>
                      <option value="precio_especial">
                        {t('menu.itemForm.promotions.types.precio_especial')}
                      </option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="item-promo-badge">{t('menu.itemForm.promotions.badge')}</label>
                    <input
                      id="item-promo-badge"
                      type="text"
                      value={formData.promotionBadge}
                      onChange={(e) => setFormData({ ...formData, promotionBadge: e.target.value })}
                      placeholder={t('menu.itemForm.promotions.badgePlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label id="item-highlight-color-label">
                      {t('menu.itemForm.promotions.highlightColor')}
                    </label>
                    <div className="promo-color-picker">
                      <input
                        type="color"
                        value={formData.highlightColor || '#E91E63'}
                        onChange={(e) =>
                          setFormData({ ...formData, highlightColor: e.target.value })
                        }
                        aria-labelledby="item-highlight-color-label"
                      />
                      <input
                        type="text"
                        value={formData.highlightColor}
                        onChange={(e) =>
                          setFormData({ ...formData, highlightColor: e.target.value })
                        }
                        placeholder="#E91E63"
                        aria-labelledby="item-highlight-color-label"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="item-promo-from">
                        {t('menu.itemForm.promotions.validFrom')}
                      </label>
                      <input
                        id="item-promo-from"
                        type="date"
                        value={formData.promotionValidFrom}
                        onChange={(e) =>
                          setFormData({ ...formData, promotionValidFrom: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="item-promo-to">{t('menu.itemForm.promotions.validTo')}</label>
                      <input
                        id="item-promo-to"
                        type="date"
                        value={formData.promotionValidTo}
                        onChange={(e) =>
                          setFormData({ ...formData, promotionValidTo: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {submitError && (
          <div className="alert alert-error item-form-submit-error" role="alert">
            {submitError}
          </div>
        )}

        {/* ── Botones ── */}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary">
            {submitLabel || t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ItemForm;
